/**
 * Lo que el paciente trae de admisión y triaje, en modo lectura.
 *
 * Se escribe una sola vez y se muestra en los dos sitios donde el médico lo
 * necesita: la ficha de la cita y la pantalla de atender. El motivo de consulta
 * ya lo dijo el paciente en admisión y lo confirmó enfermería; que el médico
 * tenga que volver a preguntarlo porque la pantalla no se lo enseña convierte
 * el triaje en papeleo.
 */
import { type Triaje } from "../../api/triaje";
import { NIVELES_URGENCIA } from "./TriajeDialog";

export function signosVitales(t: Triaje): string[] {
  return [
    t.pa_sistolica ? `PA ${t.pa_sistolica}/${t.pa_diastolica ?? "—"}` : null,
    t.fc ? `FC ${t.fc}` : null,
    t.fr ? `FR ${t.fr}` : null,
    t.temperatura ? `T° ${t.temperatura}` : null,
    t.sat_o2 ? `SatO₂ ${t.sat_o2}%` : null,
    t.peso ? `${t.peso} kg` : null,
    t.talla ? `${t.talla} cm` : null,
  ].filter((x): x is string => Boolean(x));
}

export function TriajeResumen({
  triaje,
  fecha,
}: {
  triaje: Triaje;
  /** Ya formateada en la zona del policlínico por quien llama. */
  fecha?: string;
}) {
  const vitales = signosVitales(triaje);
  return (
    <div className="space-y-2 text-sm text-slate-700">
      <p className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded px-2 py-0.5 text-xs font-semibold ${
            NIVELES_URGENCIA[triaje.nivel_urgencia]?.color ?? "bg-slate-100"
          }`}
        >
          {NIVELES_URGENCIA[triaje.nivel_urgencia]?.label ??
            `Nivel ${triaje.nivel_urgencia}`}
        </span>
        {fecha && <span className="text-slate-500">{fecha}</span>}
        {triaje.tomado_por_nombre && (
          <span className="text-slate-500">· Tomado por {triaje.tomado_por_nombre}</span>
        )}
        {triaje.especialidad_sugerida_nombre && (
          <span className="text-slate-500">
            · Sugerido a {triaje.especialidad_sugerida_nombre}
          </span>
        )}
      </p>

      {/* Lo primero que hay que leer, no una nota al pie. */}
      <div className="rounded-lg bg-slate-50 px-3 py-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Motivo de consulta
        </p>
        <p className="text-slate-900">{triaje.motivo_consulta}</p>
      </div>

      <p className="font-mono text-xs text-slate-600">
        {vitales.join(" · ") || "Sin signos vitales registrados"}
      </p>

      {triaje.observaciones && (
        <p className="text-xs italic text-slate-500">{triaje.observaciones}</p>
      )}
    </div>
  );
}
